import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Sparkles,
  Archive,
  ChevronRight,
  Calculator,
  ListChecks,
  Layers,
  Hammer,
  EyeOff,
  Eye,
} from 'lucide-react';
import { Button, VisualBlockRenderer, cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { generateInstance } from '@jeraisonne/db/blueprint';
import {
  useBlueprint,
  useUpdateBlueprintStatus,
  useMaterializeParametricExercise,
  useFillInstanceBank,
  useBlueprintInstances,
  useSetInstanceActive,
} from '@/lib/blueprint/api';
import { toExerciseVisualBlock } from '@/lib/blueprint/generated-visual-to-block';
import { BlueprintSpecEditor } from '@/components/blueprint-spec-editor';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-xp-100 text-xp-700',
  draft: 'bg-ink-100 text-ink-600',
  archived: 'bg-accent-50 text-accent-600',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Actif',
  draft: 'Brouillon',
  archived: 'Archivé',
};

/** Banque considérée comme suffisante pour activer le générateur. */
const MIN_BANK_SIZE = 10;

/**
 * Route tuteur `/tutor/blueprints/$blueprintId` — revue d'un générateur
 * paramétrique.
 *
 * ⚠️ Modèle « UN exercice paramétré » (migration 0229) : ce Blueprint pilote UN
 * SEUL exercice, dont les valeurs sont fixées PAR LA TENTATIVE de l'élève. On
 * matérialise donc l'exercice une fois (rendu canonique), puis on alimente une
 * BANQUE de tirages. Tout est 100% côté client (moteur pur `generateInstance`) :
 * AUCUN appel IA, seulement des calculs.
 */
export function TutorBlueprintDetailPage() {
  const { blueprintId } = useParams({ from: '/app/tutor/blueprints/$blueprintId' });
  const blueprint = useBlueprint(blueprintId);
  const updateStatus = useUpdateBlueprintStatus();
  const materialize = useMaterializeParametricExercise();
  const fillBank = useFillInstanceBank();
  const setInstanceActive = useSetInstanceActive();
  const instances = useBlueprintInstances(blueprintId);

  const [count, setCount] = useState(20);
  const [error, setError] = useState<string | null>(null);

  // Aperçu du visuel (graphique/tableau) avec des valeurs d'exemple — preuve
  // concrète que le Blueprint sait reproduire le visuel scanné AVANT d'activer
  // le générateur. Aucun appel IA (même moteur pur que la génération réelle).
  // Hook déclaré AVANT les retours anticipés ci-dessous (règle des Hooks).
  const specForPreview = blueprint.data?.spec;
  const visualPreview = useMemo(() => {
    if (!specForPreview?.visual) return null;
    try {
      return generateInstance(specForPreview, { seed: 'preview' }).visual ?? null;
    } catch {
      return null;
    }
  }, [specForPreview]);

  if (blueprint.isLoading) {
    return <p className="text-ink-500 mx-auto max-w-3xl px-4 py-8 text-sm">Chargement…</p>;
  }
  if (!blueprint.data) {
    return <p className="text-ink-500 mx-auto max-w-3xl px-4 py-8 text-sm">Introuvable.</p>;
  }
  const data = blueprint.data;
  const spec = data.spec;
  const allInstances = instances.data ?? [];
  const activeInstances = allInstances.filter(
    (i) => i.is_active && i.blueprint_version === data.version,
  );
  const canActivate = !!data.exercise_id && activeInstances.length > 0;

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <div className="mb-4 flex items-center gap-2">
        <Link
          to="/tutor/blueprints"
          className="text-ink-500 hover:text-ink-700 flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center"
          aria-label="Retour"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-ink-900 truncate text-xl font-extrabold leading-tight">
            {data.title}
          </h1>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className="text-ink-500 text-xs">{LEVEL_LABEL[data.level] ?? data.level}</span>
            <span className="text-ink-300">·</span>
            <span className="text-ink-500 text-xs">v{data.version}</span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
                STATUS_STYLES[data.status],
              )}
            >
              {STATUS_LABEL[data.status]}
            </span>
          </div>
        </div>
      </div>

      {data.status === 'draft' && (
        <div className="bg-accent-50 border-accent-200 text-accent-700 mb-4 flex flex-col gap-2 rounded-xl border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <span>
            {canActivate
              ? 'Relis la spec, puis active le générateur.'
              : "Matérialise l'exercice et alimente la banque avant d'activer."}
          </span>
          <Button
            onClick={() => updateStatus.mutate({ id: data.id, status: 'active' })}
            disabled={updateStatus.isPending || !canActivate}
          >
            <span className="flex items-center justify-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Activer
            </span>
          </Button>
        </div>
      )}

      {error && (
        <div className="bg-error-50 border-error-100 text-error-700 mb-4 flex items-start gap-2 rounded-xl border px-3 py-2 text-sm">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* L'UNIQUE exercice paramétrique. Son texte est le rendu CANONIQUE :
          c'est ce que voient les surfaces sans tentative (Live School, défis
          sociaux, showcase, recherche, éditeur). */}
      <section className="border-ink-200 mb-4 space-y-3 rounded-2xl border bg-white p-4 sm:p-5">
        <h2 className="font-display text-ink-900 flex items-center gap-2 text-sm font-bold">
          <Hammer className="h-4 w-4" /> Exercice paramétrique
        </h2>
        <p className="text-ink-500 text-xs">
          Un seul exercice, dont les nombres sont fixés à chaque tentative d&apos;élève. Le texte
          enregistré est le rendu canonique (celui qu&apos;affichent les écrans sans tentative).
        </p>

        {data.exercise_id ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Link
              to="/tutor/exercises/$exerciseId"
              params={{ exerciseId: data.exercise_id }}
              className="hover:bg-ink-50 text-ink-800 flex min-h-[2.75rem] flex-1 items-center justify-between gap-2 rounded-lg px-2 text-sm active:scale-[0.98]"
            >
              <span className="truncate">{data.title}</span>
              <ChevronRight className="text-ink-300 h-4 w-4 shrink-0" />
            </Link>
            <Button
              variant="secondary"
              onClick={() => run(() => materialize.mutateAsync({ blueprint: data }))}
              disabled={materialize.isPending}
            >
              {materialize.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Reconstruction…
                </span>
              ) : (
                'Reconstruire les blocs'
              )}
            </Button>
          </div>
        ) : (
          <Button
            fullWidth
            onClick={() => run(() => materialize.mutateAsync({ blueprint: data }))}
            disabled={materialize.isPending}
          >
            {materialize.isPending ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Matérialisation…
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Hammer className="h-4 w-4" /> Matérialiser l&apos;exercice
              </span>
            )}
          </Button>
        )}
        {data.exercise_id && (
          <p className="text-ink-400 text-[11px]">
            Reconstruire réécrit les blocs depuis la spec courante et désactive toute la banque (les
            calques d&apos;instances sont indexés par bloc — il faut en régénérer).
          </p>
        )}
      </section>

      {/* La banque de tirages. */}
      <section className="border-ink-200 mb-6 space-y-3 rounded-2xl border bg-white p-4 sm:p-5">
        <h2 className="font-display text-ink-900 flex items-center gap-2 text-sm font-bold">
          <Layers className="h-4 w-4" /> Banque d&apos;instances
          <span className="text-ink-400 font-normal">
            ({activeInstances.length} active{activeInstances.length > 1 ? 's' : ''})
          </span>
        </h2>
        <p className="text-ink-500 text-xs">
          Chaque instance est un jeu de valeurs : mêmes questions, mêmes distracteurs, mêmes
          raisonnements — d&apos;autres nombres. Aucun nouvel appel IA.
        </p>

        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="number"
            min={1}
            max={200}
            value={count}
            onChange={(e) => setCount(Math.min(200, Math.max(1, Number(e.target.value) || 1)))}
            aria-label="Nombre d'instances à générer"
            className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 text-sm sm:w-24"
          />
          <Button
            onClick={() => run(() => fillBank.mutateAsync({ blueprint: data, count }))}
            disabled={fillBank.isPending || !data.exercise_id}
            fullWidth
          >
            {fillBank.isPending ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Génération…
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Sparkles className="h-4 w-4" /> Ajouter {count} instance{count > 1 ? 's' : ''}
              </span>
            )}
          </Button>
        </div>

        {activeInstances.length > 0 && activeInstances.length < MIN_BANK_SIZE && (
          <p className="text-accent-600 text-[11px]">
            Banque courte ({activeInstances.length}) — en dessous de {MIN_BANK_SIZE}, les élèves
            reverront vite les mêmes nombres.
          </p>
        )}

        {instances.isLoading ? (
          <p className="text-ink-500 text-xs">Chargement…</p>
        ) : allInstances.length === 0 ? (
          <p className="text-ink-500 text-xs">
            Banque vide — tant qu&apos;elle l&apos;est, tous les élèves jouent le rendu canonique.
          </p>
        ) : (
          <ul className="divide-ink-100 max-h-80 divide-y overflow-y-auto">
            {allInstances.map((instance) => {
              const stale = instance.blueprint_version !== data.version;
              return (
                <li
                  key={instance.id}
                  className={cn(
                    'flex items-center gap-2 py-1.5',
                    (!instance.is_active || stale) && 'opacity-50',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="text-ink-800 block truncate text-xs">
                      {Object.entries(instance.payload?.parameters ?? {})
                        .map(([k, v]) => `${k} = ${v}`)
                        .join(' · ') || instance.seed}
                    </span>
                    <span className="text-ink-400 text-[11px]">
                      servie {instance.serve_count}×
                      {stale ? ` · v${instance.blueprint_version}` : ''}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      run(() =>
                        setInstanceActive.mutateAsync({
                          blueprintId: data.id,
                          instanceId: instance.id,
                          isActive: !instance.is_active,
                        }),
                      )
                    }
                    aria-label={
                      instance.is_active ? 'Retirer cette instance' : 'Remettre en service'
                    }
                    className="text-ink-400 hover:text-ink-700 flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center"
                  >
                    {instance.is_active ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {data.status === 'active' && (
          <button
            type="button"
            onClick={() => updateStatus.mutate({ id: data.id, status: 'archived' })}
            disabled={updateStatus.isPending}
            className="text-ink-500 hover:text-ink-700 flex min-h-[2.75rem] items-center gap-1.5 text-xs font-semibold"
          >
            <Archive className="h-3.5 w-3.5" /> Archiver ce générateur
          </button>
        )}
      </section>

      {/* Revue de la spec */}
      <section className="space-y-3">
        <BlueprintSpecEditor blueprint={data} />

        <h2 className="font-display text-ink-900 text-sm font-bold">Paramètres</h2>
        <div className="flex flex-wrap gap-2">
          {spec.parameters.map((p) => (
            <span
              key={p.name}
              className="border-ink-200 text-ink-700 rounded-full border bg-white px-2.5 py-1 text-xs"
            >
              {p.name} ∈ [{p.min}, {p.max}]{p.decimals ? ` (${p.decimals} déc.)` : ''}
            </span>
          ))}
        </div>

        <h2 className="font-display text-ink-900 pt-2 text-sm font-bold">Solve Path</h2>
        <div className="space-y-2">
          {spec.solvePath.map((step) => (
            <div key={step.id} className="border-ink-200 rounded-xl border bg-white p-3">
              <div className="mb-1 flex items-center gap-2">
                <span className="bg-primary-50 text-primary-600 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase">
                  {step.label}
                </span>
                {step.computation && (
                  <span className="text-ink-400 text-[11px]">
                    {step.computation.functionId}({step.computation.args.join(', ')}) →{' '}
                    {step.computation.resultName}
                  </span>
                )}
              </div>
              <p className="text-ink-800 text-sm">{step.text_md}</p>
            </div>
          ))}
        </div>

        <h2 className="font-display text-ink-900 pt-2 text-sm font-bold">
          Templates d&apos;énoncé
        </h2>
        <div className="space-y-2">
          {spec.templates.map((t) => (
            <p
              key={t.id}
              className="border-ink-200 text-ink-700 rounded-xl border bg-white p-3 text-sm"
            >
              {t.text_md}
            </p>
          ))}
        </div>

        {visualPreview && (
          <>
            <h2 className="font-display text-ink-900 pt-2 text-sm font-bold">Visuel</h2>
            <p className="text-ink-500 text-xs">
              Aperçu avec des valeurs d&apos;exemple — chaque instance générée redessine ce{' '}
              {visualPreview.type === 'table' ? 'tableau' : 'graphique'} avec ses propres valeurs.
            </p>
            <div className="border-ink-200 rounded-xl border bg-white p-3">
              <VisualBlockRenderer block={toExerciseVisualBlock(visualPreview)} />
            </div>
          </>
        )}

        <h2 className="font-display text-ink-900 pt-2 text-sm font-bold">Questions</h2>
        <div className="space-y-2">
          {spec.questions.map((q) => (
            <div key={q.id} className="border-ink-200 rounded-xl border bg-white p-3">
              <div className="mb-1 flex items-center gap-2">
                {q.answerMode === 'calculator' ? (
                  <span className="bg-accent-50 text-accent-600 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase">
                    <Calculator className="h-3 w-3" /> Calculatrice
                  </span>
                ) : (
                  <span className="bg-primary-50 text-primary-600 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase">
                    <ListChecks className="h-3 w-3" /> QCM
                  </span>
                )}
                <span className="text-ink-400 text-[11px]">résultat : {q.resultName}</span>
              </div>
              <p className="text-ink-800 text-sm">{q.prompt_md}</p>
              {q.answerMode === 'qcm' && q.distractorRules && (
                <p className="text-ink-400 mt-1 text-[11px]">
                  {q.distractorRules.length} règle{q.distractorRules.length > 1 ? 's' : ''} de
                  distracteur
                </p>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
